import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore, useAuthInit } from './hooks/useAuth';
import ErrorBoundary from './components/ErrorBoundary';
import Layout from './components/layout/Layout';
import LoginPage from './pages/LoginPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import UmgezogenPage from './pages/UmgezogenPage';
import HeutePage from './pages/HeutePage';
import KundenPage from './pages/KundenPage';
import KundePage from './pages/KundePage';
import LeadsPage from './pages/LeadsPage';
import LeadDetailPage from './pages/LeadDetailPage';
import CustomersPage from './pages/CustomersPage';
import ProjectsPage from './pages/ProjectsPage';
import EinrichtungPage from '@/pages/EinrichtungPage';
import SubmissionsPage from './pages/SubmissionsPage';
import CRMPage from './pages/CRMPage';
import NewTodosPage from './pages/NewTodosPage';
import LivePage from './pages/LivePage';
import DokumentationPage from './pages/DokumentationPage';
import StatistikenPage from './pages/StatistikenPage';
import AbrechnungPage from './pages/AbrechnungPage';
import AnalyticsPage from './pages/AnalyticsPage';
import MonatsberichtPage from './pages/MonatsberichtPage';
import PotenzialPage from '@/pages/PotenzialPage';
import VertriebUmgezogen from '@/pages/VertriebUmgezogen';
import FinanzPage from './pages/FinanzPage';
import LeitwartePage from '@/pages/leitwarte/LeitwartePage';

/**
 * Auth Provider - initialisiert Auth beim App-Start
 */
function AuthProvider({ children }: { children: React.ReactNode }) {
  useAuthInit();
  return <>{children}</>;
}

/**
 * Protected Route Component mit Timeout
 */
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuthStore();
  const [showTimeout, setShowTimeout] = useState(false);

  useEffect(() => {
    // Nach 5 Sekunden Timeout-Meldung zeigen (aber NICHT abbrechen)
    const timer = setTimeout(() => {
      if (loading) {
        console.warn('[App] Loading taking longer than expected...');
        setShowTimeout(true);
      }
    }, 5000);

    return () => clearTimeout(timer);
  }, [loading]);

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        {showTimeout && (
          <div className="mt-4 text-center">
            <p className="text-gray-600">Authentifizierung läuft...</p>
            <button
              onClick={() => {
                console.log('[App] User manually skipped to login');
                useAuthStore.setState({ user: null, loading: false });
              }}
              className="mt-2 text-primary-600 hover:underline"
            >
              Zum Login wechseln
            </button>
          </div>
        )}
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Nach Publivo umgezogene Kunden (customers.publivo_umgezogen_am, Migration 100) sehen nur noch den
  // Umzugs-Hinweis – Admin und Vertrieb arbeiten unverändert weiter.
  if (user.role === 'customer' && user.customer?.publivo_umgezogen_am) {
    return <UmgezogenPage />;
  }

  return <>{children}</>;
}

/**
 * Admin-only Route: schützt Admin-Seiten auch bei direktem URL-Aufruf.
 * (Die Sidebar blendet sie nur aus – das allein ist KEIN Schutz. Daten sind
 * zwar per RLS abgesichert, aber z.B. die Dokumentation ist statischer Inhalt.)
 */
function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user } = useAuthStore();
  if (user?.role !== 'admin') {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

/**
 * Vertriebs-Route (Nico, 06.09.2026): Betriebe, CRM und To-Dos dürfen Admin UND
 * die Rolle „vertrieb" — der Mitarbeiter, der später die Anrufliste abarbeitet.
 * Alles andere bleibt AdminRoute; die RLS-Policies aus Migration 073 decken
 * genau diese Tabellen (betriebe, chancen, crm_*) ab.
 */
function VertriebRoute({ children }: { children: React.ReactNode }) {
  const { user } = useAuthStore();
  if (user?.role !== 'admin' && user?.role !== 'vertrieb') {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

/**
 * Startseite je Rolle: Admin → Leitwarte (seit 24.09.2026, die alte Heute-Seite
 * bleibt unter /heute), Vertrieb → Betriebe (die Heute-Seite schickt
 * Nicht-Admins zu /leads, das wäre für den Vertrieb die falsche Ecke).
 */
function StartSeite() {
  const { user } = useAuthStore();
  if (user?.role === 'admin') return <Navigate to="/leitwarte" replace />;
  if (user?.role === 'vertrieb') return <Navigate to="/betriebe" replace />;
  return <HeutePage />;
}

/**
 * App Component mit Routing
 */
function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            {/* Public Routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />

            {/* Leitwarte: eigene Vollbild-Seite ohne LMS-Rahmen, nur Admin */}
            <Route
              path="/leitwarte"
              element={
                <ProtectedRoute>
                  <AdminRoute>
                    <LeitwartePage />
                  </AdminRoute>
                </ProtectedRoute>
              }
            />

            {/* Protected Routes */}
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }
            >
              <Route index element={<StartSeite />} />
              <Route path="heute" element={<AdminRoute><HeutePage /></AdminRoute>} />
              <Route path="kunden" element={<AdminRoute><KundenPage /></AdminRoute>} />
              <Route path="kunden/verwalten" element={<AdminRoute><CustomersPage /></AdminRoute>} />
              <Route path="kunden/:id" element={<AdminRoute><KundePage /></AdminRoute>} />
              <Route path="analytics" element={<AnalyticsPage />} />
              {/* Live-Ansicht aus dem eigenen Tracking (12.09.2026) — bewusst
                  nur fuer Nico: Kunden bekommen die Seite nicht. */}
              <Route path="analytics/live" element={<AdminRoute><LivePage /></AdminRoute>} />
              <Route path="monatsbericht" element={<MonatsberichtPage />} />
              <Route path="finanzen" element={<AdminRoute><FinanzPage /></AdminRoute>} />
              <Route path="potenzial" element={<AdminRoute><PotenzialPage /></AdminRoute>} />
              {/* Vertrieb ist am 13.10.2026 nach Publivo umgezogen (dashboard.publivo.app/admin/vertrieb). */}
              <Route path="betriebe" element={<VertriebRoute><VertriebUmgezogen /></VertriebRoute>} />
              <Route path="betriebe/:id" element={<VertriebRoute><VertriebUmgezogen /></VertriebRoute>} />
              {/* Hiess bis 12.09.2026 „Cockpit"; die alte Adresse bleibt als Weiterleitung. */}
              <Route path="vertrieb/analyse" element={<VertriebRoute><VertriebUmgezogen /></VertriebRoute>} />
              <Route path="vertrieb/cockpit" element={<Navigate to="/vertrieb/analyse" replace />} />
              {/* Der Leadpool ist seit 06.09.2026 die Kandidaten-Stufe der Betriebe */}
              <Route path="leadpool" element={<Navigate to="/betriebe" replace />} />
              <Route path="statistiken" element={<AdminRoute><StatistikenPage /></AdminRoute>} />
              <Route path="crm" element={<VertriebRoute><CRMPage /></VertriebRoute>} />
              {/* /todos ist seit 12.09.2026 die Vertriebsseite (Arbeitsvorrat,
                  Ablauf, Regeln). Die alte Aufgaben- und Projektliste ist
                  nicht weg, sie liegt eine Ebene tiefer. */}
              <Route path="todos" element={<VertriebRoute><VertriebUmgezogen /></VertriebRoute>} />
              <Route path="todos/aufgaben" element={<VertriebRoute><NewTodosPage /></VertriebRoute>} />
              <Route path="dokumentation" element={<AdminRoute><DokumentationPage /></AdminRoute>} />
              <Route path="leads" element={<LeadsPage />} />
              <Route path="leads/:id" element={<LeadDetailPage />} />
              {/* Alte Adresse: die Verwaltungsseite lebt jetzt unter /kunden/verwalten */}
              <Route path="customers" element={<Navigate to="/kunden/verwalten" replace />} />
              <Route path="projects" element={<AdminRoute><ProjectsPage /></AdminRoute>} />
              <Route path="einrichtung" element={<AdminRoute><EinrichtungPage /></AdminRoute>} />
              <Route path="submissions" element={<SubmissionsPage />} />
              <Route path="abrechnung" element={<AbrechnungPage />} />
            </Route>

            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
